"use client";

import { AgentActivity, type AgentActivityItem } from "@/components/agents/agent-activity";
import { Message, MessageBubble, MessageBubbleContent } from "@/components/agents/message";
import { ChatComposer } from "@/components/chat-composer";
import { buildHistoryEntries, formatHistoryTime } from "@/components/editor/history-entries";
import { ModuleGlyph } from "@/components/editor/panels/module-glyphs";
import { PanelEmpty, PanelHeader, PanelSearch } from "@/components/editor/panels/panel-chrome";
import { FieldShell } from "@/components/editor/schema-form/field-shell";
import { moduleStyleCapabilities } from "@/components/handyout-components/modules/module-style-capabilities";
import { HandyoutPaletteScope } from "@/components/handyout-palette-scope";
import { AnimatedBadge } from "@/components/motion/animated-badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/motion/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { getModuleChatSuggestions } from "@/lib/module-chat-suggestions";
import {
  getModuleCategory,
  MODULE_CATEGORY_ORDER,
  MODULE_TYPE_META,
  MODULE_TYPE_NAMES,
} from "@/lib/module-type-meta";
import { cn } from "@/lib/utils";
import { HANDYOUT_SECTION_KEYS, type ModuleRevisionBatch } from "@/types/handyout-modules";
import {
  MODULE_ACCENT_SLOT_COUNT,
  MODULE_ALIGN_VALUES,
  MODULE_SPACING_VALUES,
  MODULE_STYLE_DEFAULTS,
  type AllModuleTypeNames,
  type ModuleStyle,
  type PageModule,
} from "@/types/modules";
import {
  AlignCenter,
  AlignLeft,
  ChevronDown,
  GripVertical,
  Plus,
  Sparkles,
  Trash2,
} from "lucide-react";
import { useId, useMemo, useState, type ReactNode } from "react";
import { StaticButton } from "./pre-cards";
import {
  PRE_BASE_COLOR,
  PRE_CHAT_SLIDE_ID,
  PRE_HISTORY_BATCHES,
  PRE_HISTORY_TIMES,
  PRE_SLIDE_PAGES,
  PRE_SLIDES,
} from "./pre-fixture";
import { ProductDevice } from "./product-device";

type InspectorTab = "chat" | "styles" | "content";

const INSPECTOR_TABS: { value: InspectorTab; label: string }[] = [
  { value: "chat", label: "Chat" },
  { value: "styles", label: "Styles" },
  { value: "content", label: "Content" },
];

type StyleState = {
  accent?: number;
  spacing: (typeof MODULE_SPACING_VALUES)[number];
  align: (typeof MODULE_ALIGN_VALUES)[number];
  showTitle: boolean;
  grayscale: boolean;
};

const DEFAULT_STYLE: StyleState = {
  spacing: MODULE_STYLE_DEFAULTS.spacing,
  align: MODULE_STYLE_DEFAULTS.align,
  showTitle: MODULE_STYLE_DEFAULTS.showTitle,
  grayscale: false,
};

const SPACING_LABEL: Record<StyleState["spacing"], string> = {
  compact: "Compact",
  default: "Default",
  relaxed: "Relaxed",
};

const ALIGN_META: Record<StyleState["align"], { label: string; icon: ReactNode }> = {
  left: { label: "Left", icon: <AlignLeft className="size-3.5" /> },
  center: { label: "Center", icon: <AlignCenter className="size-3.5" /> },
};

const SLOTS = Array.from({ length: MODULE_ACCENT_SLOT_COUNT }, (_, slot) => slot);

function noop() {}

function InspectorCrop({
  title,
  typeLabel,
  tab,
  onTabChange,
  className,
  children,
}: {
  title: string;
  typeLabel: string;
  tab: InspectorTab;
  onTabChange: (tab: InspectorTab) => void;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={cn("flex w-full min-w-0 flex-col text-left", className)}>
      <div className="flex h-11 min-w-0 shrink-0 items-center gap-2 px-3">
        <span className="min-w-0 truncate text-[13px] font-medium">{title || typeLabel}</span>
        <AnimatedBadge size="sm" status="neutral" showIcon={false} className="min-w-0 shrink truncate">
          <span className="truncate">{typeLabel}</span>
        </AnimatedBadge>
      </div>
      <Tabs
        value={tab}
        onValueChange={(value) => onTabChange(value as InspectorTab)}
        variant="underline"
        className="shrink-0"
      >
        <TabsList className="flex w-full gap-0 px-1">
          {INSPECTOR_TABS.map((item) => (
            <TabsTrigger key={item.value} value={item.value} className="min-h-9 px-2.5 pt-1 pb-2 text-[13px]">
              {item.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">{children}</div>
    </div>
  );
}

function StyleSection({ label, hint, children }: { label: string; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
      {hint ? <p className="text-[11px] leading-snug text-muted-foreground/80">{hint}</p> : null}
    </div>
  );
}

function SwitchRow({
  label,
  hint,
  checked,
  onCheckedChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  onCheckedChange: (next: boolean) => void;
}) {
  const id = useId();
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <div className="min-w-0">
        <label htmlFor={id} className="block text-[13px] text-foreground">
          {label}
        </label>
        <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} className="shadow-none" />
    </div>
  );
}

function StylesPane({
  type,
  style,
  onChange,
  scopeId,
}: {
  type: AllModuleTypeNames;
  style: StyleState;
  onChange: (next: StyleState) => void;
  scopeId: string;
}) {
  const capabilities = moduleStyleCapabilities(type);
  const patch = (next: Partial<StyleState>) => onChange({ ...style, ...next });
  const accentHint = style.grayscale
    ? "Grayscale is on, so the module ignores its accent."
    : style.accent === undefined
      ? "Default uses the primary color for single marks and the palette order for series."
      : `Palette color ${style.accent + 1} leads. Series start from this color.`;

  return (
    <div className="flex flex-col gap-5 p-3">
      {capabilities.accent ? (
        <StyleSection label="Accent from palette" hint={accentHint}>
          <HandyoutPaletteScope primaryColor={PRE_BASE_COLOR} paletteScheme="analog" scopeId={scopeId}>
            <div
              role="radiogroup"
              aria-label="Accent color"
              aria-disabled={style.grayscale || undefined}
              className={cn(
                "grid grid-cols-6 gap-1.5 transition-opacity",
                style.grayscale && "pointer-events-none opacity-40",
              )}
            >
              <button
                type="button"
                role="radio"
                aria-checked={style.accent === undefined}
                aria-label="Default accent, primary color"
                title="Default"
                disabled={style.grayscale}
                onClick={() => patch({ accent: undefined })}
                className={cn(
                  "relative flex h-8 items-center justify-center rounded-md border border-dashed border-border bg-background outline-none transition-colors focus-visible:border-ring",
                  style.accent === undefined && "border-solid border-primary focus-visible:border-primary",
                )}
              >
                <span className="size-3.5 rounded-full" style={{ background: "var(--primary)" }} />
              </button>
              {SLOTS.map((slot) => (
                <button
                  key={slot}
                  type="button"
                  role="radio"
                  aria-checked={style.accent === slot}
                  aria-label={`Palette color ${slot + 1}`}
                  title={`Palette color ${slot + 1}`}
                  disabled={style.grayscale}
                  onClick={() => patch({ accent: slot })}
                  className={cn(
                    "flex h-8 items-center justify-center rounded-md border border-black/10 outline-none transition-colors focus-visible:border-ring",
                    style.accent === slot && "border-primary focus-visible:border-primary",
                  )}
                  style={{ background: `var(--handyout-chart-${slot + 1})` }}
                />
              ))}
            </div>
          </HandyoutPaletteScope>
        </StyleSection>
      ) : null}

      {capabilities.spacing ? (
        <StyleSection label="Spacing">
          <Tabs
            value={style.spacing}
            onValueChange={(next) => patch({ spacing: next as StyleState["spacing"] })}
            variant="segment"
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-3">
              {MODULE_SPACING_VALUES.map((value) => (
                <TabsTrigger
                  key={value}
                  value={value}
                  className="h-6 w-full px-2 text-xs"
                >
                  {SPACING_LABEL[value]}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </StyleSection>
      ) : null}

      {capabilities.align ? (
        <StyleSection label="Alignment">
          <Tabs
            value={style.align}
            onValueChange={(next) => patch({ align: next as StyleState["align"] })}
            variant="segment"
            className="w-full"
          >
            <TabsList className="grid w-full grid-cols-2">
              {MODULE_ALIGN_VALUES.map((value) => (
                <TabsTrigger
                  key={value}
                  value={value}
                  className="h-6 w-full gap-1.5 px-2 text-xs"
                >
                  {ALIGN_META[value].icon}
                  {ALIGN_META[value].label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </StyleSection>
      ) : null}

      {capabilities.showTitle || capabilities.grayscale ? (
        <div className="flex flex-col border-t border-border pt-2">
          {capabilities.showTitle ? (
            <SwitchRow
              label="Show title"
              hint="The module title above its content"
              checked={style.showTitle}
              onCheckedChange={(next) => patch({ showTitle: next })}
            />
          ) : null}
          {capabilities.grayscale ? (
            <SwitchRow
              label="Grayscale"
              hint={capabilities.accent ? "Render in monochrome. Overrides the accent." : "Render in monochrome."}
              checked={style.grayscale}
              onCheckedChange={(next) => patch({ grayscale: next })}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const id = useId();
  return (
    <FieldShell label={label} htmlFor={id}>
      <Input
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-8 text-[13px] md:text-[13px]"
      />
    </FieldShell>
  );
}

function ListHeader({
  label,
  count,
  max,
  onAdd,
}: {
  label: string;
  count: number;
  max: number;
  onAdd: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <div className="flex min-w-0 items-baseline gap-1.5">
        <span className="truncate text-[13px] font-medium text-foreground">{label}</span>
        <span className="shrink-0 text-[11px] tabular-nums text-muted-foreground">
          {count}/{max}
        </span>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={`Add ${label.toLowerCase().replace(/s$/, "")}`}
        disabled={count >= max}
        onClick={onAdd}
        className="text-muted-foreground hover:text-foreground"
      >
        <Plus />
      </Button>
    </div>
  );
}

type ListItem = { id: string; label: string };

function ListRows({
  items,
  min,
  onRemove,
}: {
  items: ListItem[];
  min: number;
  onRemove: (id: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      {items.map((item, index) => (
        <div key={item.id} className="rounded-md border border-border bg-background">
          <div className="flex h-9 items-center gap-1 pr-1 pl-1">
            <span className="flex h-7 w-5 shrink-0 items-center justify-center text-muted-foreground">
              <GripVertical className="size-3.5" />
            </span>
            <span className="flex h-full min-w-0 flex-1 items-center gap-2">
              <span className="w-5 shrink-0 text-[11px] tabular-nums text-muted-foreground">{index + 1}</span>
              <span className="min-w-0 flex-1 truncate text-[13px] text-foreground">{item.label}</span>
              <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              aria-label={`Remove item ${index + 1}`}
              disabled={items.length <= min}
              onClick={() => onRemove(item.id)}
              className="text-muted-foreground hover:text-destructive"
            >
              <Trash2 />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function SuggestionsPane({ type, moduleTitle }: { type: AllModuleTypeNames; moduleTitle: string }) {
  const suggestions = getModuleChatSuggestions(type);
  return (
    <div className="flex flex-col items-center px-6 py-8 text-center">
      <div className="max-w-[17rem] text-[15px] leading-snug font-medium">
        What would you like to change about your {moduleTitle}?
      </div>
      <p className="mt-1.5 max-w-xs text-sm leading-relaxed text-neutral-500">
        Describe it in your own words, or start from one of these.
      </p>
      <div className="mt-6 w-full max-w-sm space-y-1.5">
        {suggestions.map((suggestion) => (
          <div
            key={suggestion}
            className="flex w-full items-center rounded-lg px-3 py-2.5 text-left text-sm text-neutral-500 ring-1 ring-neutral-200"
          >
            <span className="truncate">{suggestion}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const CHAT_ACTIVITY: AgentActivityItem[] = [
  { id: "read", type: "tool", action: "read", target: "your slide content" },
  { id: "edit", type: "tool", action: "edit", target: "your module" },
];

type ProposalState = "pending" | "accepted" | "declined";

const PROPOSAL_NOTE = "The proposed changes are shown in the preview. Nothing is saved until you accept.";

const PROPOSAL_FORM_NOTE =
  "The preview shows the assistant's proposal. The fields below show the saved module; editing them declines the proposal.";

function ProposalCardView({
  note = PROPOSAL_NOTE,
  onAccept,
  onDecline,
}: {
  note?: string;
  onAccept: () => void;
  onDecline: () => void;
}) {
  return (
    <div className="flex w-full min-w-0 flex-col gap-2.5 rounded-md border border-dashed border-primary/60 bg-muted/40 p-3">
      <AnimatedBadge size="sm" status="info" showIcon={false} className="w-fit">
        Proposed
      </AnimatedBadge>
      <p className="text-xs text-muted-foreground">{note}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" size="sm" onClick={onAccept}>
          Accept changes
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onDecline}>
          Decline
        </Button>
      </div>
    </div>
  );
}

function ChatPane({
  request,
  proposal,
  onProposal,
}: {
  request: string;
  proposal: ProposalState;
  onProposal: (next: ProposalState) => void;
}) {
  const [draft, setDraft] = useState("");
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4 py-3">
        <Message from="user">
          <MessageBubble variant="solid">
            <MessageBubbleContent>{request}</MessageBubbleContent>
          </MessageBubble>
        </Message>
        <AgentActivity items={CHAT_ACTIVITY} status="complete" />
        {proposal === "pending" ? (
          <ProposalCardView onAccept={() => onProposal("accepted")} onDecline={() => onProposal("declined")} />
        ) : null}
      </div>
      <div className="shrink-0 border-t bg-background p-3">
        <ChatComposer
          value={draft}
          onChange={setDraft}
          onSubmit={noop}
          placeholder="Tell us what you want to change or add to this module..."
          rows={2}
          disabled
        />
      </div>
    </div>
  );
}

const CHAT_SLIDE = PRE_SLIDE_PAGES.find((page) => page.slideId === PRE_CHAT_SLIDE_ID) ?? PRE_SLIDE_PAGES[0]!;
const CHAT_MODULE = CHAT_SLIDE.modules[0] as PageModule["modules"][number] & {
  title?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
};
const CHAT_TYPE = CHAT_MODULE.type as AllModuleTypeNames;

function moduleStyle(style: StyleState, grayscale: boolean): Partial<ModuleStyle> & { grayscale: boolean } {
  return {
    accent: style.accent,
    spacing: style.spacing === MODULE_STYLE_DEFAULTS.spacing ? undefined : style.spacing,
    align: style.align === MODULE_STYLE_DEFAULTS.align ? undefined : style.align,
    showTitle: style.showTitle ? undefined : false,
    grayscale,
  };
}

export function ChatEditCrop() {
  const [tab, setTab] = useState<InspectorTab>("chat");
  const [proposal, setProposal] = useState<ProposalState>("pending");
  const [style, setStyle] = useState<StyleState>(DEFAULT_STYLE);
  const [title, setTitle] = useState(CHAT_MODULE.title ?? "");
  const [xAxis, setXAxis] = useState(CHAT_MODULE.xAxisLabel ?? "");
  const [yAxis, setYAxis] = useState(CHAT_MODULE.yAxisLabel ?? "");

  const resolve = (next: ProposalState) => {
    setProposal(next);
    if (next === "accepted") setStyle((current) => ({ ...current, grayscale: true }));
  };

  const edit = <T,>(setter: (value: T) => void) => (value: T) => {
    if (proposal === "pending") setProposal("declined");
    setter(value);
  };

  const proposalCard =
    proposal === "pending" ? (
      <div className="px-3 pt-3">
        <ProposalCardView
          note={PROPOSAL_FORM_NOTE}
          onAccept={() => resolve("accepted")}
          onDecline={() => resolve("declined")}
        />
      </div>
    ) : null;

  const pages = useMemo<PageModule[]>(() => {
    const grayscale = proposal === "pending" ? true : style.grayscale;
    return [
      {
        ...CHAT_SLIDE,
        modules: [
          {
            ...CHAT_MODULE,
            ...moduleStyle(style, grayscale),
            title,
            xAxisLabel: xAxis,
            yAxisLabel: yAxis,
          } as PageModule["modules"][number],
        ],
      },
    ];
  }, [proposal, style, title, xAxis, yAxis]);

  const typeLabel = MODULE_TYPE_META[CHAT_TYPE].label;

  return (
    <div className="flex w-full max-w-[780px] items-end justify-center gap-6 lg:gap-10">
      <div className="hidden h-[440px] shrink-0 overflow-hidden sm:block">
        <HandyoutPaletteScope primaryColor={PRE_BASE_COLOR} paletteScheme="analog" scopeId="pre-chat-device">
          <ProductDevice pages={pages} scale={0.66} />
        </HandyoutPaletteScope>
      </div>
      <div className="h-[440px] w-full max-w-[400px] min-w-0 overflow-hidden rounded-t-xl border border-b-0 border-neutral-200 bg-white shadow-[0_16px_40px_-24px_rgba(15,23,42,0.35)]">
        <InspectorCrop title={title} typeLabel={typeLabel} tab={tab} onTabChange={setTab} className="h-full">
          {tab === "chat" ? (
            <ChatPane request="Render it in greyscale" proposal={proposal} onProposal={resolve} />
          ) : tab === "styles" ? (
            <>
              {proposalCard}
              <StylesPane type={CHAT_TYPE} style={style} onChange={edit(setStyle)} scopeId="pre-chat-styles" />
            </>
          ) : (
            <>
              {proposalCard}
              <div className="flex flex-col gap-4 p-3">
                <TextField label="Title" value={title} onChange={edit(setTitle)} />
                <TextField label="X axis label" value={xAxis} onChange={edit(setXAxis)} />
                <TextField label="Y axis label" value={yAxis} onChange={edit(setYAxis)} />
              </div>
            </>
          )}
        </InspectorCrop>
      </div>
    </div>
  );
}

const STEP_MIN = 4;
const STEP_MAX = 8;

const INITIAL_STEPS: ListItem[] = [
  { id: "kickoff", label: "Kickoff" },
  { id: "discovery", label: "Discovery" },
  { id: "design", label: "Design" },
  { id: "build", label: "Build" },
  { id: "review", label: "Review" },
  { id: "launch", label: "Launch" },
];

export function FormsCrop() {
  const [tab, setTab] = useState<InspectorTab>("content");
  const [title, setTitle] = useState("Release schedule");
  const [steps, setSteps] = useState<ListItem[]>(INITIAL_STEPS);
  const [style, setStyle] = useState<StyleState>(DEFAULT_STYLE);
  const type: AllModuleTypeNames = "timeline-stepper";

  const addStep = () =>
    setSteps((current) =>
      current.length >= STEP_MAX
        ? current
        : [...current, { id: `step-${current.length + 1}-${Date.now()}`, label: `Step ${current.length + 1}` }],
    );

  return (
    <InspectorCrop title={title} typeLabel={MODULE_TYPE_META[type].label} tab={tab} onTabChange={setTab}>
      {tab === "content" ? (
        <div className="flex flex-col gap-4 p-3">
          <TextField label="Title" value={title} onChange={setTitle} />
          <div className="flex flex-col gap-2">
            <ListHeader label="Steps" count={steps.length} max={STEP_MAX} onAdd={addStep} />
            <ListRows
              items={steps}
              min={STEP_MIN}
              onRemove={(id) => setSteps((current) => current.filter((item) => item.id !== id))}
            />
          </div>
        </div>
      ) : tab === "styles" ? (
        <StylesPane type={type} style={style} onChange={setStyle} scopeId="pre-forms-styles" />
      ) : (
        <SuggestionsPane type={type} moduleTitle={title || MODULE_TYPE_META[type].label} />
      )}
    </InspectorCrop>
  );
}

const SLIDE_NUMBERS = new Map(PRE_SLIDES.map((slide) => [slide.slideId, slide.number]));

export function HistoryCrop() {
  const [restores, setRestores] = useState<{ batch: ModuleRevisionBatch; time: string }[]>([]);
  const [openId, setOpenId] = useState<string | null>(PRE_HISTORY_BATCHES[2]?.batch_id ?? null);

  const entries = useMemo(() => {
    const batches = [...restores.map((item) => item.batch), ...PRE_HISTORY_BATCHES];
    const times = [...restores.map((item) => item.time), ...PRE_HISTORY_TIMES];
    return buildHistoryEntries(batches, SLIDE_NUMBERS).map((entry, index) => ({
      ...entry,
      time: times[index] ?? entry.time,
    }));
  }, [restores]);

  const restore = (batchId: string) => {
    const source = [...restores.map((item) => item.batch), ...PRE_HISTORY_BATCHES].find(
      (batch) => batch.batch_id === batchId,
    );
    if (!source) return;
    const now = new Date().toISOString();
    setRestores((current) => [
      {
        batch: {
          ...source,
          batch_id: `pre-restore-${current.length + 1}`,
          source: "restore",
          created_at: now,
          updated_at: now,
        },
        time: formatHistoryTime(now),
      },
      ...current,
    ]);
    setOpenId(null);
  };

  return (
    <div className="flex w-full flex-col text-left">
      <PanelHeader title="History" />
      <ul className="flex flex-col gap-0.5 px-2" aria-label="Edit history">
        {entries.map(({ batchId, label, icon: Icon, time, detail }, index) => {
          const open = openId === batchId;
          const current = index === 0;
          return (
            <li key={batchId}>
              <div className={cn("rounded-md transition-colors", open ? "bg-accent" : "hover:bg-accent/60")}>
                <button
                  type="button"
                  onClick={() => {
                    if (!current) setOpenId(open ? null : batchId);
                  }}
                  aria-expanded={current ? undefined : open}
                  className="flex w-full min-w-0 items-start gap-2 px-2 py-1.5 text-left"
                >
                  <Icon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1">
                    <span className="flex min-w-0 items-baseline justify-between gap-2">
                      <span className="truncate text-[13px] text-foreground">{label}</span>
                      <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums">{time}</span>
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {current ? ["Current version", detail].filter(Boolean).join(" · ") : detail}
                    </span>
                  </span>
                </button>
                {open && !current ? (
                  <div className="px-2 pb-2 pl-7.5">
                    <Button type="button" size="sm" variant="outline" onClick={() => restore(batchId)}>
                      Restore this version
                    </Button>
                  </div>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const GENERAL_TYPES: ReadonlySet<string> = new Set(HANDYOUT_SECTION_KEYS);

function ModuleTile({ type }: { type: AllModuleTypeNames }) {
  return (
    <div className="flex h-full w-full min-w-0 flex-col items-stretch gap-1.5 rounded-md border border-border bg-background p-1.5">
      <span className="flex h-14 w-full min-w-0 items-center justify-center rounded-[5px] bg-muted/50 px-2 py-1.5">
        <ModuleGlyph type={type} className="h-full w-full" />
      </span>
      <span className="w-full min-w-0 truncate px-0.5 text-xs leading-tight text-foreground">
        {MODULE_TYPE_META[type].label}
      </span>
    </div>
  );
}

export function NewModuleCrop() {
  const [query, setQuery] = useState("");
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    return MODULE_CATEGORY_ORDER.map((category) => ({
      ...category,
      types: MODULE_TYPE_NAMES.filter((type) => {
        if (GENERAL_TYPES.has(type)) return false;
        if (getModuleCategory(type) !== category.id) return false;
        if (!q) return true;
        return `${MODULE_TYPE_META[type].label} ${type} ${category.label}`.toLowerCase().includes(q);
      }),
    })).filter((group) => group.types.length > 0);
  }, [query]);

  return (
    <div className="flex w-full flex-col text-left">
      <PanelHeader title="New module">
        <span className="truncate pr-1 text-xs text-muted-foreground">Adds to slide 3</span>
      </PanelHeader>
      <PanelSearch value={query} onChange={setQuery} placeholder="Search modules" />
      <div className="h-[340px] overflow-hidden px-3">
        {groups.length === 0 ? (
          <PanelEmpty
            className="mx-0"
            title="No module found"
            text={`Nothing matches "${query.trim()}". Try a shorter term.`}
          />
        ) : (
          groups.map((group) => (
            <section key={group.id} className="mt-3 first:mt-1">
              <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                <span>{group.label}</span>
                <span className="tabular-nums">{group.types.length}</span>
              </div>
              <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-4 lg:grid-cols-6">
                {group.types.map((type) => (
                  <ModuleTile key={type} type={type} />
                ))}
              </div>
            </section>
          ))
        )}
      </div>
    </div>
  );
}

type FillState = "fresh" | "filled" | "manual";

const FILL_TYPE: AllModuleTypeNames = "metric-tabs-chart";

const FILL_CONTENT: Record<"demo" | "filled", { title: string; metrics: string[] }> = {
  demo: { title: "Business development", metrics: ["Revenue", "New customers", "Utilisation", "Churn rate"] },
  filled: { title: "Revenue and costs", metrics: ["Revenue", "Costs"] },
};

function fillItems(metrics: string[]): ListItem[] {
  return metrics.map((metric) => ({ id: metric.toLowerCase(), label: metric }));
}

export function FillWithAiCrop() {
  const [state, setState] = useState<FillState>("fresh");
  const [title, setTitle] = useState(FILL_CONTENT.demo.title);
  const [metrics, setMetrics] = useState<ListItem[]>(() => fillItems(FILL_CONTENT.demo.metrics));
  const label = MODULE_TYPE_META[FILL_TYPE].label;

  const fill = () => {
    setState("filled");
    setTitle(FILL_CONTENT.filled.title);
    setMetrics(fillItems(FILL_CONTENT.filled.metrics));
  };

  return (
    <div className="flex w-full flex-col gap-4 text-left">
      <div className="relative mx-2 mt-3 rounded-md ring-1 ring-primary">
        <div className="absolute top-0 left-2 flex max-w-[calc(100%-1rem)] -translate-y-1/2 items-center gap-1.5">
          <span className="min-w-0 truncate rounded-[4px] bg-primary px-1.5 py-px text-[11px] font-medium text-primary-foreground">
            {label}
          </span>
          {state === "fresh" ? (
            <StaticButton size="xs">
              <Sparkles className="size-3" />
              Fill with AI
            </StaticButton>
          ) : null}
        </div>
        <div className="flex h-24 items-center justify-center rounded-md bg-muted/50 px-6 pt-3 pb-2">
          <ModuleGlyph type={FILL_TYPE} className="h-full w-full" />
        </div>
      </div>

      {state === "fresh" ? (
        <div className="flex flex-col gap-2.5 rounded-md border border-border bg-muted/40 p-3">
          <p className="text-xs text-muted-foreground">This module shows demo content.</p>
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" onClick={fill}>
              <Sparkles className="size-3.5" />
              Fill with AI
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setState("manual")}>
              Edit manually
            </Button>
          </div>
        </div>
      ) : null}

      <TextField label="Title" value={title} onChange={setTitle} />
      <div className="flex flex-col gap-1.5">
        <span className="text-xs text-muted-foreground">Metrics</span>
        <ListRows
          items={metrics}
          min={1}
          onRemove={(id) => setMetrics((current) => current.filter((item) => item.id !== id))}
        />
      </div>
    </div>
  );
}
